import { Class, Delete, EmojiEvents, Quickreply, TapAndPlay, ViewCarousel } from "@mui/icons-material";
import { Alert, AlertTitle, Autocomplete, Box, CircularProgress, FormControl, Grid, IconButton, InputLabel, MenuItem, Paper, Select, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import Swal from "sweetalert2";
import { apiV2 } from "../../actions";
import FieldSetGroup from "../Elements/FieldSetGroup";
import { COMMAND_KEYS, CONTROL_KEYS } from "../../constants/keys";


const InitiativeForm = ({ initiative, setInitiative, error, isModified, setIsModified }) => {
    const refResources = useRef();

    const [selectedResource, setSelectedResource] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedResourceType, setSelectedResourceType] = useState('');
    const [resources, setResources] = useState([]);

    const resourceTypes = [
        {
            name: 'Challenge',
            code: 'challenge',
        },
        {
            name: 'Series',
            code: 'series',
        },
        {
            name: 'Session',
            code: 'session',
        },
        {
            name: 'QuickTap Session',
            code: 'quick-tap-session',
        },
        {
            name: 'Card Decks',
            code: 'deck',
        }
    ];

    useEffect(() => {
        handleLoadResources();
    }, [selectedResourceType]);

    const handleChange = (e) => {
        if (!isModified) setIsModified(true);

        let value = e.target.value;
        if (e.target.name === 'code') {
            value = value.replace(/[^a-z0-9]/gi, '').toUpperCase();
        }

        setInitiative({ ...initiative, [e.target.name]: value });
    }

    const handleChangeResource = (e, value) => {
        if (!isModified) setIsModified(true);
        setSelectedResource(value);
        handleAddResource(value);
    }

    const handleChangeResourceType = (e) => {
        setSelectedResourceType(e.target.value || '');
    }

    const handleLoadResources = () => {
        if (!selectedResourceType || selectedResourceType === '') {
            setResources([]);
            return;
        }

        if (selectedResourceType === 'challenge') {
            fetchChallenges();
        }

        if (selectedResourceType === 'series') {
            fetchSeries();
        }

        if (selectedResourceType === 'session') {
            fetchSessions();
        }

        if (selectedResourceType === 'quick-tap-session') {
            fetchQuickTapSessions();
        }

        if (selectedResourceType === 'deck') {
            fetchDecks();
        }
    }

    const handleSetResources = resources => {
        setResources(resources);
        setTimeout(() => {
            refResources.current.focus();
        }, 200);
    }

    const fetchDecks = () => {
        setIsLoading(true);
        apiV2.get('/decks')
            .then((response) => {
                const decks = (response.data.objects || []).map((deck) => ({
                    id: deck.id,
                    name: deck.name,
                    type: 'deck',
                    key: `deck-${deck.id}`,
                    icon: <ViewCarousel sx={{ fontSize: '1rem', mr: 1 }} />
                }));
                handleSetResources(decks);
            })
            .catch((error) => {
                handleFetchResourceError();
            })
            .finally(() => {
                setIsLoading(false);
            })
    }

    const fetchChallenges = () => {
        setIsLoading(true);

        apiV2.get(`/challenges`)
            .then((response) => {
                const items = response.data.objects || response.data || [];
                const challenges = items.map((challenge) => ({
                    id: challenge.challenge_id,
                    name: challenge.challenge_title,
                    type: 'challenge',
                    key: `challenge-${challenge.challenge_id}`,
                    icon: <EmojiEvents sx={{ fontSize: '1rem', mr: 1 }} />
                }));
                handleSetResources(challenges);
            })
            .catch((error) => {
                handleFetchResourceError();
            })
            .finally(() => {
                setIsLoading(false);
            })
    }

    const fetchSeries = () => {
        setIsLoading(true);
        apiV2.get(`/series`)
            .then((response) => {
                const items = response.data.objects || response.data || [];
                const series = items.map((series) => ({
                    id: series.series_id,
                    name: series.series_title,
                    type: 'series',
                    key: `series-${series.series_id}`,
                    icon: <Class sx={{ fontSize: '1rem', mr: 1 }} />
                }));
                handleSetResources(series);
            })
            .catch((error) => {
                handleFetchResourceError();
            })
            .finally(() => {
                setIsLoading(false);
            })
    }

    const fetchSessions = () => {
        setIsLoading(true);
        apiV2.get(`/sessions`)
            .then((response) => {
                const items = response.data.objects || response.data || [];
                const sessions = items.map((session) => ({
                    id: session.session_id,
                    name: session.session_name,
                    type: 'session',
                    key: `session-${session.session_id}`,
                    icon: <TapAndPlay sx={{ fontSize: '1rem', mr: 1 }} />
                }));
                handleSetResources(sessions);
            })
            .catch((error) => {
                handleFetchResourceError();
            })
            .finally(() => {
                setIsLoading(false);
            })
    }

    const fetchQuickTapSessions = () => {
        setIsLoading(true);
        apiV2.get(`/quick-tap-sessions`)
            .then((response) => {
                const items = response.data.objects || response.data || [];
                const quickTapSessions = items.map((quickTapSession) => ({
                    id: quickTapSession.id,
                    name: quickTapSession.title,
                    type: 'quick-tap-session',
                    key: `quick-tap-session-${quickTapSession.id}`,
                    icon: <Quickreply sx={{ fontSize: '1rem', mr: 1 }} />
                }));
                handleSetResources(quickTapSessions);
            })
            .catch((error) => {
                handleFetchResourceError();
            })
            .finally(() => {
                setIsLoading(false);
            })
    }

    const handleFetchResourceError = () => {
        Swal.fire({
            icon: "error",
            title: `Error fetching ${selectedResourceType}`,
        });
    }

    const handleAddResource = resource => {
        if (!resource) {
            return;
        }

        if (initiative.resources.some(r => r.key === resource.key)) {
            return;
        }

        setInitiative({
            ...initiative,
            resources: [...initiative.resources, resource]
        });

        setTimeout(() => {
            setSelectedResource(null);
        }, 100);
    }

    const handleRemoveResource = resource => {
        setInitiative({
            ...initiative,
            resources: initiative.resources.filter(r => r.key !== resource.key)
        });
    }

    const handleKeyDown = (e) => {
        const key = e.key;

        if (key === ' ') {
            e.preventDefault();
            return;
        }

        const isAllowedControlKey = CONTROL_KEYS.includes(key);

        if (isAllowedControlKey) {
            return;
        }

        if (e.ctrlKey || e.metaKey) {
            if (COMMAND_KEYS.includes(key.toLowerCase())) {
                return;
            }
        }

        const isAlphanumeric = /^[a-z0-9]$/i.test(key);
        if (!isAlphanumeric) {
            e.preventDefault();
        }
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
                error={error.name}
                fullWidth
                label="Title"
                variant="outlined"
                name="name"
                value={initiative.name ?? ""}
                onChange={handleChange}
                autoFocus
                helperText="Sample format: Sleep Support Challenge - Facebook Ads"
                required
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                    error={error.code}
                    fullWidth
                    label="Code"
                    variant="outlined"
                    name="code"
                    value={initiative.code ?? ""}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    helperText="Unique code for the campaign. This works as a identifier for the initiative."
                    required
                    sx={{ flex: 3 }}
                />
                <TextField
                    error={error.default_unlock_duration}
                    type="number"
                    fullWidth
                    label="Content Unlock Duration"
                    variant="outlined"
                    name="default_unlock_duration"
                    value={initiative.default_unlock_duration ?? ""}
                    onChange={handleChange}
                    helperText="Amount of days the content will be unlocked for the user. Not retroactive."
                    required
                    sx={{ flex: 1 }}
                />
            </Box>
            <TextField
                label="Description"
                fullWidth
                variant="outlined"
                name="description"
                multiline
                minRows={3}
                value={initiative.description ?? ""}
                onChange={handleChange}
                helperText="Sample format: This initiative is created for landing pages linked from Facebook Ads promoting the Evergreen Sleep Support program."
            />
            <FieldSetGroup title="Initiative Resources *">
                <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                    <FormControl sx={{ minWidth: 200, flex: 1 }}>
                        <InputLabel id="resource-type-label">Resource type</InputLabel>
                        <Select
                            labelId="resource-type-label"
                            label="Resource type"
                            name="resource_type"
                            value={selectedResourceType}
                            onChange={handleChangeResourceType}
                            disabled={isLoading}
                            required
                        >
                            {
                                resourceTypes.map((resourceType, index) => (
                                    <MenuItem key={index} value={resourceType.code}>
                                        {resourceType.name}
                                    </MenuItem>
                                ))
                            }
                        </Select>
                    </FormControl>
                    <FormControl sx={{ flex: 3, minWidth: 300 }}>
                        <Autocomplete
                            id='resource-select'
                            error={error.resources}
                            disablePortal
                            options={resources}
                            getOptionLabel={(option) => option.name}
                            getOptionKey={(option) => option.id}
                            value={selectedResource || null}
                            disabled={isLoading}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Resource"
                                    variant="outlined"
                                    placeholder="Select a resource"
                                    inputRef={input => {
                                        refResources.current = input;
                                    }}
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <React.Fragment>
                                                {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                                {params.InputProps.endAdornment}
                                            </React.Fragment>
                                        ),
                                    }}
                                />
                            )}
                            loading={isLoading}
                            onChange={handleChangeResource}
                            noOptionsText="No resource found"
                            loadingText="Loading data..."
                            clearText="Clear"
                            isOptionEqualToValue={(option, value) => option.id === value?.id}
                            renderOption={(props, option) => (
                                <Box
                                    component="li"
                                    {...props}
                                    key={option.id}
                                >
                                    {option.icon}
                                    {option.name}
                                </Box>
                            )}
                        />
                    </FormControl>
                </Box>
                {
                    initiative.resources.length === 0 && (
                        <Alert severity={error.resources ? 'error' : 'info'}>
                            <AlertTitle>No resources added</AlertTitle>
                            Select a resource to add to the initiative. When you select a resource, it will be automatically added to the initiative.
                        </Alert>
                    )
                }
                {
                    initiative.resources.length > 0 && (
                        <TableContainer
                            component={Paper}
                            sx={{
                                marginTop: 1,
                                boxShadow: 2,
                                borderRadius: 1,
                                overflow: 'hidden',
                            }}
                        >
                            <Table>
                                <TableHead>
                                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                        <TableCell>Resource</TableCell>
                                        <TableCell align="center">Type</TableCell>
                                        <TableCell width="10%" align="center">Remove</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {
                                        initiative.resources.map((resource) => (
                                            <TableRow key={resource.id}>
                                                <TableCell>{resource.name}</TableCell>
                                                <TableCell align="center">{resource.type}</TableCell>
                                                <TableCell align="center">
                                                    <Tooltip title="Remove resource">
                                                        <IconButton onClick={() => handleRemoveResource(resource)}>
                                                            <Delete />
                                                        </IconButton>
                                                    </Tooltip>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    }
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )
                }
            </FieldSetGroup>
        </Box>
    )
}

export default InitiativeForm;